/**
 * Scraping Rules API Hooks
 *
 * TanStack Query hooks for managing domain-specific scraping rules.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_BASE, getAuthHeaders } from './client';

// ============================================================================
// Types
// ============================================================================

export interface SelectorConfig {
    selector: string;
    fallbacks?: string[];
    attribute?: 'textContent' | 'innerHTML' | 'href' | 'src';
    transform?: 'trim' | 'markdown';
}

export interface ScrapingRuleSelectors {
    title: SelectorConfig;
    company: SelectorConfig;
    description: SelectorConfig;
    location?: SelectorConfig;
    salary?: SelectorConfig;
    remoteType?: SelectorConfig;
    mainContent?: string;
    removeSelectors?: string[];
}

export interface ScrapingRule {
    id: string;
    domain: string;
    domain_pattern?: string;
    selectors: ScrapingRuleSelectors;
    version: number;
    confidence_score: number;
    success_count: number;
    failure_count: number;
    last_used_at: string;
    last_success_at?: string;
    last_failure_at?: string;
    is_active: boolean;
    created_by: 'llm' | 'user';
    created_at: string;
    updated_at: string;
}

export interface ScrapingRuleStats {
    total: number;
    active: number;
    inactive: number;
    avgConfidence: number;
}

export interface TestRuleResult {
    success: boolean;
    confidence: number;
    fieldsExtracted: string[];
    fieldsMissing: string[];
    extractedData?: {
        title: string;
        company: string;
        description: string;
        location?: string;
        salary?: string;
    };
}

export interface LearnResult {
    ruleId: string;
    domain: string;
    confidence: number;
    selectors: ScrapingRuleSelectors;
    tokensUsed: {
        input: number;
        output: number;
        total: number;
    };
    jobData: {
        title: string;
        company: string;
        description: string;
    };
}

// ============================================================================
// Query Keys
// ============================================================================

export const scrapingRulesKeys = {
    all: ['scraping-rules'] as const,
    stats: ['scraping-rules', 'stats'] as const,
    detail: (id: string) => ['scraping-rules', id] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all scraping rules with stats.
 */
export function useScrapingRules() {
    return useQuery({
        queryKey: scrapingRulesKeys.all,
        queryFn: async () => {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/scraping-rules`, { headers });

            if (!res.ok) {
                throw new Error('Failed to fetch scraping rules');
            }

            const data = await res.json();
            return data as {
                data: ScrapingRule[];
                stats: ScrapingRuleStats;
            };
        },
    });
}

/**
 * Fetch a single scraping rule by ID.
 */
export function useScrapingRule(id: string) {
    return useQuery({
        queryKey: scrapingRulesKeys.detail(id),
        queryFn: async () => {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/scraping-rules/${id}`, { headers });

            if (!res.ok) {
                throw new Error('Failed to fetch scraping rule');
            }

            const data = await res.json();
            return data.data as ScrapingRule;
        },
        enabled: !!id,
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Toggle a rule's active status.
 */
export function useToggleScrapingRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/scraping-rules/${id}/toggle`, {
                method: 'PUT',
                headers,
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to toggle rule');
            }

            const data = await res.json();
            return data.data as ScrapingRule;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: scrapingRulesKeys.all });
        },
    });
}

/**
 * Delete a scraping rule.
 */
export function useDeleteScrapingRule() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/scraping-rules/${id}`, {
                method: 'DELETE',
                headers,
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to delete rule');
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: scrapingRulesKeys.all });
        },
    });
}

/**
 * Test a rule against a URL.
 */
export function useTestScrapingRule() {
    return useMutation({
        mutationFn: async ({ ruleId, testUrl }: { ruleId: string; testUrl: string }) => {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/scraping-rules/test`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ ruleId, testUrl }),
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to test rule');
            }

            const data = await res.json();
            return data.data as TestRuleResult;
        },
    });
}

/**
 * Manually trigger LLM learning for a URL.
 */
export function useLearnFromUrl() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (url: string) => {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_BASE}/scraping-rules/learn`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ url }),
            });

            if (!res.ok) {
                const error = await res.json();
                throw new Error(error.error || 'Failed to learn from URL');
            }

            const data = await res.json();
            return data.data as LearnResult;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: scrapingRulesKeys.all });
        },
    });
}
